import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  UserIcon, 
  KeyIcon, 
  ComputerDesktopIcon, 
  CpuChipIcon,
  CalendarIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { Modal, Tabs, Badge, Card, CardContent, CardHeader, CardTitle, Button } from './ui';
import { clientService } from '../services/clientService';
import { licenseService } from '../services/licenseService';
import { formatDate } from '../utils/formatters';
import { CLIENT_TYPES, CLIENT_STATUS, LICENSE_STATUS } from '../constants';

const ClientDetailModal = ({ isOpen, onClose, clientId }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const queryClient = useQueryClient();

  // Fetch client details (includes licenses and installations)
  const { data: client, isLoading: clientLoading } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => clientService.getClient(clientId),
    enabled: !!clientId && isOpen,
  });

  const releaseInstallationMutation = useMutation({
    mutationFn: ({ licenseId, installationId }) =>
      licenseService.releaseInstallation(licenseId, installationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
    },
  });

  const handleReleaseInstallation = (installation) => {
    const licenseId = installation.license_id;
    if (!licenseId || !installation.id) {
      window.alert('No se pudo identificar la licencia o la instalación.');
      return;
    }
    const ok = window.confirm(
      `¿Liberar este equipo y recuperar un cupo de instalación?\n\n` +
        `${installation.machine_name}\n${installation.mac_address}\n\n` +
        `La licencia ${installation.license_key} podrá activarse en un equipo nuevo.`
    );
    if (!ok) return;
    releaseInstallationMutation.mutate({
      licenseId,
      installationId: installation.id,
    });
  };

  // Extract data from client response
  const licenses = client?.licenses || [];
  const installations =
    client?.licenses?.flatMap((license) =>
      (license.installations || []).map((inst) => ({
        ...inst,
        license_id: inst.license_id ?? license.id,
      }))
    ) || [];

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'suspended': return 'danger';
      case 'pending': return 'warning';
      default: return 'secondary';
    }
  };

  const getTypeLabel = (type) => {
    const typeObj = CLIENT_TYPES.find(t => t.value === type);
    return typeObj ? typeObj.label : type;
  };

  const getStatusLabel = (status) => {
    const statusObj = CLIENT_STATUS.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  const getLicenseTypeLabel = (license) => {
    // Usar directamente el campo license_type que ahora contiene el nombre del tipo definido por el usuario
    return license.license_type || 'Sin tipo';
  };

  const getLicenseStatusLabel = (status) => {
    const statusObj = LICENSE_STATUS.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  const getLicenseStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'danger';
      case 'suspended': return 'warning';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  const getLicenseTypeBadgeVariant = (license) => {
    // Usar un color basado en el ID del LicenseType para consistencia
    const typeId = license.license_type_config || 0;
    const colors = ['info', 'success', 'warning', 'danger', 'secondary', 'primary'];
    return colors[typeId % colors.length];
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Hace un momento';
    if (diffInSeconds < 3600) return `Hace ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `Hace ${Math.floor(diffInSeconds / 3600)} h`;
    if (diffInSeconds < 2592000) return `Hace ${Math.floor(diffInSeconds / 86400)} días`;
    if (diffInSeconds < 31536000) return `Hace ${Math.floor(diffInSeconds / 2592000)} meses`;
    return `Hace ${Math.floor(diffInSeconds / 31536000)} años`;
  };

  const isLicenseExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const isInstallationActive = (installation) => {
    // Si no hay heartbeat, considerar activa si la instalación está marcada como activa
    if (!installation.last_heartbeat) {
      return installation.is_active;
    }
    
    // Si hay heartbeat, verificar si es reciente (menos de 24 horas)
    const lastHeartbeatDate = new Date(installation.last_heartbeat);
    const now = new Date();
    const diffInHours = (now - lastHeartbeatDate) / (1000 * 60 * 60);
    return diffInHours < 24 && installation.is_active;
  };

  // Overview Tab Content
  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Client Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <UserIcon className="h-5 w-5 mr-2" />
            Información del Cliente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Nombre</label>
                <p className="text-gray-900">{client?.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Email</label>
                <p className="text-gray-900 flex items-center">
                  <EnvelopeIcon className="h-4 w-4 mr-1" />
                  {client?.email}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Empresa</label>
                <p className="text-gray-900 flex items-center">
                  <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                  {client?.company}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Tipo de Cliente</label>
                <Badge variant="info" className="mt-1">
                  {getTypeLabel(client?.client_type)}
                </Badge>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Estado</label>
                <div className="mt-1">
                  <Badge variant={getStatusBadgeVariant(client?.status)}>
                    {getStatusLabel(client?.status)}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Teléfono</label>
                <p className="text-gray-900 flex items-center">
                  <PhoneIcon className="h-4 w-4 mr-1" />
                  {client?.phone}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Dirección</label>
                <p className="text-gray-900 flex items-center">
                  <MapPinIcon className="h-4 w-4 mr-1" />
                  {client?.address}, {client?.city}, {client?.state}
                </p>
                {client?.postal_code && (
                  <p className="text-sm text-gray-500 ml-5">
                    CP: {client.postal_code}, {client?.country}
                  </p>
                )}
              </div>
              {client?.website && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Sitio Web</label>
                  <p className="text-gray-900 flex items-center">
                    <GlobeAltIcon className="h-4 w-4 mr-1" />
                    <a href={client.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {client.website}
                    </a>
                  </p>
                </div>
              )}
              {client?.phone_secondary && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Teléfono Secundario</label>
                  <p className="text-gray-900 flex items-center">
                    <PhoneIcon className="h-4 w-4 mr-1" />
                    {client.phone_secondary}
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <KeyIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Licencias Totales</p>
                <p className="text-2xl font-bold text-gray-900">{client?.license_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <ComputerDesktopIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Instalaciones</p>
                <p className="text-2xl font-bold text-gray-900">{client?.installation_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <CalendarIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Cliente desde</p>
                <p className="text-sm font-bold text-gray-900">{formatDate(client?.created_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Info */}
      {(client?.contact_person || client?.notes || client?.tax_id) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <DocumentTextIcon className="h-5 w-5 mr-2" />
              Información Adicional
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {client?.contact_person && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Persona de Contacto</label>
                  <p className="text-gray-900">{client.contact_person}</p>
                  {client?.contact_position && (
                    <p className="text-sm text-gray-500">{client.contact_position}</p>
                  )}
                </div>
              )}
              {client?.tax_id && (
                <div>
                  <label className="text-sm font-medium text-gray-700">RFC/CUIT</label>
                  <p className="text-gray-900">{client.tax_id}</p>
                </div>
              )}
              {client?.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Notas</label>
                  <p className="text-gray-900">{client.notes}</p>
                </div>
              )}
              {client?.last_contact && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Último Contacto</label>
                  <p className="text-gray-900">{formatDate(client.last_contact)}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Licenses Tab Content
  const LicensesTab = () => (
    <div className="space-y-4">
      {licenses && licenses.length > 0 ? (
        <div className="space-y-4">
          {licenses.map((license) => (
            <Card key={license.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {license.software_name} v{license.software_version}
                      </h3>
                      <Badge variant={getLicenseStatusBadgeVariant(license.status)}>
                        {getLicenseStatusLabel(license.status)}
                      </Badge>
                      <Badge variant={getLicenseTypeBadgeVariant(license)}>
                        {getLicenseTypeLabel(license)}
                      </Badge>
                    </div>
                    {/* Software Information */}
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <ComputerDesktopIcon className="h-4 w-4 text-blue-600" />
                        <label className="font-medium text-gray-700">Software</label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">Nombre:</span>
                          <span className="ml-2 font-medium text-gray-900">{license.software_name}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Versión:</span>
                          <span className="ml-2 font-medium text-gray-900">{license.software_version}</span>
                        </div>
                        {license.software_description && (
                          <div className="md:col-span-2">
                            <span className="text-gray-600">Descripción:</span>
                            <p className="mt-1 text-gray-900 text-sm">{license.software_description}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Pricing Information */}
                    {license.price && (
                      <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center space-x-2 mb-2">
                          <CurrencyDollarIcon className="h-4 w-4 text-green-600" />
                          <label className="font-medium text-gray-700">Información de Precios</label>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-600">Precio Final:</span>
                            <span className="ml-2 font-bold text-green-700 text-lg">${license.price}</span>
                          </div>
                          {license.original_price && license.original_price !== license.price && (
                            <div>
                              <span className="text-gray-600">Precio Original:</span>
                              <span className="ml-2 text-gray-500 line-through">${license.original_price}</span>
                            </div>
                          )}
                          {license.discount_percentage && license.discount_percentage > 0 && (
                            <div>
                              <span className="text-gray-600">Descuento:</span>
                              <span className="ml-2 font-medium text-red-600">{license.discount_percentage}%</span>
                            </div>
                          )}
                          {license.promotion_name && (
                            <div className="md:col-span-2">
                              <span className="text-gray-600">Promoción:</span>
                              <span className="ml-2 font-medium text-blue-600">{license.promotion_name}</span>
                            </div>
                          )}
                          {license.promotion_description && (
                            <div className="md:col-span-2">
                              <span className="text-gray-600">Descripción de Promoción:</span>
                              <p className="mt-1 text-gray-700 text-sm">{license.promotion_description}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* License Information */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <label className="font-medium text-gray-700">Clave de Licencia</label>
                        <p className="text-gray-900 font-mono text-xs">{license.license_key}</p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">Instalaciones</label>
                        <p className="text-gray-900">
                          {license.current_installations} / {license.max_installations}
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">Fecha de Emisión</label>
                        <p className="text-gray-900">{formatDate(license.issued_date)}</p>
                      </div>
                    </div>
                    {license.expiry_date && (
                      <div className="mt-2">
                        <label className="font-medium text-gray-700">Fecha de Expiración</label>
                        <div className="flex items-center space-x-2">
                          <p className="text-gray-900">{formatDate(license.expiry_date)}</p>
                          {isLicenseExpired(license.expiry_date) ? (
                            <Badge variant="danger" className="text-xs">
                              <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                              Expirada
                            </Badge>
                          ) : (
                            <Badge variant="success" className="text-xs">
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Válida
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <KeyIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay licencias</h3>
          <p className="text-gray-500">Este cliente no tiene licencias asignadas.</p>
        </div>
      )}
    </div>
  );

  // Installations Tab Content
  const InstallationsTab = () => (
    <div className="space-y-4">
      {installations && installations.length > 0 ? (
        <div className="space-y-4">
          {installations.map((installation) => (
            <Card key={installation.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <CpuChipIcon className="h-5 w-5 text-gray-400" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        {installation.machine_name}
                      </h3>
                      <Badge 
                        variant={isInstallationActive(installation) ? 'success' : 'warning'}
                      >
                        {isInstallationActive(installation) ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </div>
                    {/* Software and License Information */}
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <KeyIcon className="h-4 w-4 text-green-600" />
                        <label className="font-medium text-gray-700">Software y Licencia</label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">Software:</span>
                          <span className="ml-2 font-medium text-gray-900">{installation.software_name} v{installation.software_version}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Tipo de Licencia:</span>
                          <span className="ml-2 font-medium text-gray-900">{getLicenseTypeLabel(installation)}</span>
                        </div>
                        <div className="md:col-span-2">
                          <span className="text-gray-600">Clave de Licencia:</span>
                          <p className="mt-1 font-mono text-xs text-gray-900">{installation.license_key}</p>
                        </div>
                      </div>
                    </div>

                    {/* Machine Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="md:col-span-2">
                        <label className="font-medium text-gray-700">ID de instalación</label>
                        <p className="text-gray-900 font-mono text-xs break-all">
                          {installation.client_installation_id ? (
                            installation.client_installation_id
                          ) : (
                            <>
                              <span className="text-gray-500">No asignado</span>
                              {installation.id != null && (
                                <span className="block text-gray-400 mt-0.5 text-[11px]">
                                  ID interno (sistema): {installation.id}
                                </span>
                              )}
                            </>
                          )}
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">Dirección MAC</label>
                        <p className="text-gray-900 font-mono text-xs">{installation.mac_address}</p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">ID de Máquina</label>
                        <p className="text-gray-900 font-mono text-xs">{installation.machine_id || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">IP Address</label>
                        <p className="text-gray-900">{installation.ip_address || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">Estado</label>
                        <Badge 
                          variant={isInstallationActive(installation) ? 'success' : 'warning'}
                          className="text-xs"
                        >
                          {isInstallationActive(installation) ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <label className="font-medium text-gray-700">Fecha de Instalación</label>
                        <p className="text-gray-900">{formatDate(installation.installation_date)}</p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">Último Heartbeat</label>
                        <div className="flex items-center space-x-2">
                          <p className="text-gray-900">
                            {installation.last_heartbeat ? formatDate(installation.last_heartbeat) : 'Nunca'}
                          </p>
                          {installation.last_heartbeat && (
                            <span className="text-xs text-gray-500">
                              ({getTimeAgo(installation.last_heartbeat)})
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {installation.notes && (
                      <div className="mt-2">
                        <label className="font-medium text-gray-700">Notas</label>
                        <p className="text-gray-900 text-sm">{installation.notes}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 pt-1">
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      title="Liberar cupo en esta licencia (quitar este PC)"
                      disabled={releaseInstallationMutation.isPending}
                      onClick={() => handleReleaseInstallation(installation)}
                      className="whitespace-nowrap"
                    >
                      <TrashIcon className="h-4 w-4 mr-1 inline" />
                      Liberar PC
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <ComputerDesktopIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay instalaciones</h3>
          <p className="text-gray-500">Este cliente no tiene instalaciones registradas.</p>
        </div>
      )}
    </div>
  );

  const tabs = [
    {
      id: 'overview',
      label: 'Resumen',
      icon: <UserIcon className="h-4 w-4" />,
      content: <OverviewTab />
    },
    {
      id: 'licenses',
      label: 'Licencias',
      icon: <KeyIcon className="h-4 w-4" />,
      badge: client?.license_count || 0,
      content: <LicensesTab />
    },
    {
      id: 'installations',
      label: 'Instalaciones',
      icon: <ComputerDesktopIcon className="h-4 w-4" />,
      badge: client?.installation_count || 0,
      content: <InstallationsTab />
    }
  ];

  if (clientLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Detalles del Cliente" size="xl">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Cargando información del cliente...</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Detalles del Cliente" size="xl">
      <Tabs 
        tabs={tabs} 
        defaultTab={activeTab}
        onTabChange={setActiveTab}
        className="h-full"
      />
    </Modal>
  );
};

export { ClientDetailModal };
